/**
 * "Refresh traces" — every refresh that has run against a published event.
 *
 * Replaces the old "Also seen on" card on the event edit page. That card listed
 * one row per source URL and was rewritten in place, so it could only ever say
 * where the event had been seen and when each page was last looked at. This one
 * lists **one row per completed refresh**, nothing replaced, so a curator can
 * see what each refresh did over time rather than only the current state of each
 * page.
 *
 * Sits directly under the "Tina Trace" card in the same sidebar column, and
 * answers the next question along: the trace is the item that published this
 * event, this is every refresh since. The heading matches the card above it in
 * weight and spacing, and the rows follow the conventions the sources list set —
 * muted label text, small secondary chips, a plain `title` rather than a Radix
 * tooltip (this is a read-only row and should not carry a `TooltipProvider`
 * dependency).
 *
 * **The rows are fetched, not handed in.** They come off
 * `GET /refresh-jobs?admin_event_id=<mood event id>`, through the same `tina`
 * data provider and the same `useQuery` shape `EntityTinaTrace` uses one card
 * above — an internal Tina route, not a Refine resource, so the call goes
 * through `custom` with a `meta.urlPath` rather than a registered resource.
 *
 * **Renders nothing when there is nothing to show.** An event that has never
 * been refreshed has no traces, and an empty card reads as a broken one — the
 * same rule `EntityTinaTrace` and the sources list both follow. Loading and
 * failure render nothing too: this is a side panel on someone else's page, and
 * an error card here would shout about a request the curator never asked for.
 */
import { Link } from "react-router";

import { useDataProvider } from "@refinedev/core";
import { useQuery } from "@tanstack/react-query";
import { ArrowUpRight, GitBranch, Network } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import {
  type RefreshTracesPage,
  formatComparedAt,
  readRefreshTraces,
  refreshTraceLabel,
  refreshTraceModeLabel,
} from "@/lib/refresh-traces";
import type { IRefreshTrace } from "@/types/event";

const HEADING = "Refresh traces";

/**
 * How many refreshes the card fetches.
 *
 * The route pages — `v_refresh_jobs` is one row per refresh and a nightly sweep
 * adds one per event every night — so an uncapped fetch would grow a sidebar
 * card without bound. 25 is a full screen of compact rows in a column that also
 * holds the summary and the Tina trace, and it is deep enough that the "showing
 * 25 of N" note below the list is rare rather than routine.
 */
const REFRESH_TRACES_LIMIT = 25;

interface RefreshTracesCardProps {
  /**
   * The Mood event's own id — the `admin_event_id` a refresh job carries, and
   * the one parameter the route filters on. `null`/`undefined` fetches nothing.
   */
  eventId: string | null | undefined;
  className?: string;
}

export function RefreshTracesCard({
  eventId,
  className,
}: RefreshTracesCardProps) {
  const { custom } = useDataProvider()("tina");

  const query = useQuery({
    queryKey: ["tina", "refresh-jobs", "event", eventId, REFRESH_TRACES_LIMIT],
    enabled: !!eventId,
    // The same window `EntityTinaTrace` keeps: a refresh history changes on the
    // pipeline's schedule, not on this page's.
    staleTime: 60_000,
    queryFn: async (): Promise<RefreshTracesPage> => {
      const response = await custom!({
        url: "",
        method: "get",
        query: {
          admin_event_id: eventId,
          limit: REFRESH_TRACES_LIMIT,
          offset: 0,
        },
        meta: { urlPath: "refresh-jobs" },
      });
      return readRefreshTraces(response.data);
    },
  });

  // Loading, failed, and disabled all land here, and all three render nothing —
  // see the docstring. Checked before the length so a failed request cannot fall
  // through to an empty card.
  const page = query.data;
  if (!page || page.traces.length === 0) return null;

  const { traces, total } = page;

  return (
    <Card className={className}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-sm font-semibold">{HEADING}</h2>
          {/* `total` off the response, not `traces.length`: the badge answers
              "how often has this been refreshed", which is a fact about the
              event, not about the page of it that was fetched. */}
          <Badge variant="secondary">{total}</Badge>
        </div>
        <p className="text-xs text-muted-foreground">
          Every refresh of this event, newest first. Written by the refresh
          pipeline — read-only here.
        </p>
      </CardHeader>
      <CardContent className="pt-0">
        <ul className="flex flex-col gap-2">
          {traces.map((trace, index) => (
            // Keyed on the job's own Tina id — one refresh, one id — falling
            // back to position for a row the route sent without one.
            <RefreshTraceRow key={trace.tinaItemId ?? index} trace={trace} />
          ))}
        </ul>
        {total > traces.length && (
          // Said out loud rather than left to the badge, which reads as a count
          // of what is on screen. Only ever renders when the cap was hit.
          <p className="pt-2 text-[11px] text-muted-foreground">
            showing {traces.length} of {total}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function RefreshTraceRow({ trace }: { trace: IRefreshTrace }) {
  const label = refreshTraceLabel(trace);
  const mode = refreshTraceModeLabel(trace.mode);
  const fields = trace.updatedFields ?? [];
  // `undefined` is "this orchestrator does not send `applied_fields`", which is
  // a different answer from `[]` — "this refresh changed nothing". Printing "no
  // changes" over a row whose outcome chip says `updated` would contradict
  // itself, so the whole block is dropped instead. See `lib/refresh-traces.ts`.
  const knowsWhatChanged = trace.updatedFields !== undefined;

  return (
    <li className="flex flex-col gap-1 rounded-md border bg-muted/20 p-2">
      <div className="flex flex-wrap items-center gap-2">
        {trace.url ? (
          <a
            href={trace.url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex min-w-0 items-center gap-1 text-sm font-medium hover:underline"
          >
            <span className="truncate">{label}</span>
            <ArrowUpRight className="h-3 w-3 shrink-0 text-muted-foreground" />
          </a>
        ) : (
          // `url` is nullable in this contract — a carry-site job seeded
          // not-listed has none — and a row with no href is still a row: the
          // timestamp, outcome and chips are the record of a refresh that
          // happened, so the label stays even when it cannot link.
          <span className="inline-flex min-w-0 items-center text-sm font-medium">
            <span className="truncate">{label}</span>
          </span>
        )}
        {mode && (
          <Badge variant="secondary" className="text-[10px] font-normal">
            {mode}
          </Badge>
        )}
        {trace.outcome && (
          // `outline`, so the outcome does not read as a second mode chip.
          <Badge variant="outline" className="text-[10px] font-normal">
            {trace.outcome}
          </Badge>
        )}
      </div>

      {trace.comparedAt && (
        <span className="text-xs text-muted-foreground">
          compared{" "}
          {/* Plain `title`, not `makeTooltip`: same reason as the sources list.
              `comparedAt` is UTC and the label is local, so a curator diffing
              this against a Tina log needs the value Tina actually stored. */}
          <span className="font-mono tabular-nums" title={trace.comparedAt}>
            {formatComparedAt(trace.comparedAt)}
          </span>
        </span>
      )}

      {knowsWhatChanged && (
        <div className="flex flex-wrap items-center gap-1">
          {fields.length > 0 ? (
            <>
              {/* What this refresh actually changed on the event. */}
              <span className="text-[11px] text-muted-foreground">updated</span>
              {fields.map((field) => (
                <Badge
                  key={field}
                  variant="secondary"
                  className="text-[10px] font-normal"
                >
                  {field}
                </Badge>
              ))}
            </>
          ) : (
            // Said out loud rather than left blank: a refresh that agreed with
            // what Mood already had is a result, and an empty row reads like a
            // rendering bug.
            <span className="text-[11px] text-muted-foreground">
              no changes
            </span>
          )}
        </div>
      )}

      {(trace.tinaItemId || trace.runId) && (
        <div className="flex flex-wrap items-center gap-1">
          {trace.tinaItemId && (
            // Internal SPA routes, so `Link`, not an `<a href>` — matches how
            // `EntityTinaTrace` navigates to both of these pages. `Network` and
            // `GitBranch`, not `ArrowUpRight`: that icon already means "open the
            // source URL" on the link above.
            <Link
              to={`/tina/items/show/${trace.tinaItemId}`}
              title={trace.tinaItemId}
              className="inline-flex shrink-0 items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
            >
              <Network className="h-3 w-3" />
              Tina item
            </Link>
          )}
          {trace.runId && (
            <Link
              to={`/tina/runs/show/${trace.runId}`}
              title={trace.runId}
              className="inline-flex shrink-0 items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
            >
              <GitBranch className="h-3 w-3" />
              Run
            </Link>
          )}
        </div>
      )}
    </li>
  );
}
